import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DateTimePicker } from '@nucleus-commerce/admin-kit/date-time';
import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { EditModal } from '@nucleus-commerce/admin-kit/forms';
import type {
  PostAdminV1StoresStoreIdProductsBody,
  ProductSummary,
  StaffProfileStoresItem,
} from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { fromZonedInput } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useVariants } from '@/features/products';
import { displayTimeZone } from '@/shared/store/store-context';

import {
  useProductStoreRows,
  useStorePriceRange,
  type ProductStoreRow,
} from '../hooks/use-product-store-rows';
import { useAddProductToStore } from '../hooks/use-store-product-mutations';
import { storeProductLiveState } from '../schemas/live-state';
import { handleTaken, publishConflict } from '../schemas/publication-rules';

import { PublishConflictAlert } from './publish-conflict-alert';

type PriceChoice = 'base' | 'later';
type WhenChoice = 'now' | 'schedule' | 'draft';

const WHEN_CHOICES: WhenChoice[] = ['now', 'schedule', 'draft'];
const HANDLE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

type ModalProduct = Pick<ProductSummary, 'id' | 'handle' | 'status'>;

export interface PublishToStoresModalProps {
  product: ModalProduct;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function StoreCheck({
  store,
  detail,
  checked,
  disabled,
  onChange,
}: {
  store: string;
  detail: string;
  checked: boolean;
  disabled: boolean;
  onChange?: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-2.5">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        className="mt-0.5"
        onCheckedChange={(next) => onChange?.(next === true)}
      />
      <Label htmlFor={id} className="flex min-w-0 flex-col items-start gap-0 font-normal">
        <span className={cn('font-medium', disabled && !onChange && 'text-muted-foreground')}>
          {store}
        </span>
        <span className="text-muted-foreground text-xs">{detail}</span>
      </Label>
    </div>
  );
}

/** A store the product is already in, ticked for good, with how it sells there. */
function AlreadyInStore({ entry, product }: { entry: ProductStoreRow; product: ModalProduct }) {
  const { t } = useTranslation('storeCatalog');
  const format = useFormat();
  const { store, row } = entry;
  const range = useStorePriceRange(store.id, row?.id ?? null);
  const state = row
    ? storeProductLiveState({ ...row, masterStatus: product.status })
    : ({ kind: 'draft' } as const);
  let detail: string;
  if (state.kind === 'live') {
    detail = [t('publishModal.already.live'), range].filter(Boolean).join(' · ');
  } else if (state.kind === 'scheduled') {
    detail = t('publishModal.already.scheduled', { date: format.date(state.at) });
  } else if (state.kind === 'hidden') {
    detail = t(`storePage.live.hidden.${state.master}`);
  } else {
    detail = t(`publishModal.already.${state.kind}`);
  }
  return <StoreCheck store={store.name} detail={detail} checked disabled />;
}

function Choice({
  value,
  title,
  hint,
  disabled = false,
}: {
  value: string;
  title: string;
  hint?: string | undefined;
  disabled?: boolean;
}) {
  return (
    <label className={cn('flex items-start gap-2.5', disabled ? 'opacity-50' : 'cursor-pointer')}>
      <RadioGroupItem value={value} disabled={disabled} className="mt-0.5" />
      <span className="flex min-w-0 flex-col">
        <span className="font-medium">{title}</span>
        {hint ? <span className="text-muted-foreground text-xs">{hint}</span> : null}
      </span>
    </label>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2.5 text-sm" aria-labelledby={id}>
      <h3 id={id} className="text-sm font-semibold">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** "$1,970.00 – $2,673.40 across 9 variants" from the master prices, while it can be said. */
function useBasePriceHint(productId: string): string | undefined {
  const { t } = useTranslation('storeCatalog');
  const format = useFormat();
  const variants = useVariants(productId);
  const prices = (variants.data ?? [])
    .filter((variant) => variant.status !== 'archived')
    .map((variant) => variant.price);
  const [first] = prices;
  if (!first) return undefined;
  const min = prices.reduce((low, price) => (price.amount < low.amount ? price : low), first);
  const max = prices.reduce((high, price) => (price.amount > high.amount ? price : high), first);
  return t('publishModal.prices.baseHint', {
    count: prices.length,
    range:
      min.amount === max.amount ? format.money(min) : `${format.money(min)} – ${format.money(max)}`,
  });
}

function Body({ product, onOpenChange }: Omit<PublishToStoresModalProps, 'open'>) {
  const { t } = useTranslation(['storeCatalog', 'common', 'errors']);
  const message = useErrorMessage();
  const add = useAddProductToStore();
  const { rows } = useProductStoreRows(product.id);
  const ids = useId();
  const baseHint = useBasePriceHint(product.id);

  const inStore = rows.filter((entry) => entry.row);
  const addable = rows.filter((entry) => entry.row === null && entry.store.status !== 'closed');
  const closed = rows.filter((entry) => entry.row === null && entry.store.status === 'closed');

  // Untouched, a lone store that can take the product is ticked for the person.
  const [picked, setPicked] = useState<ReadonlySet<string> | null>(null);
  const selected =
    picked ?? new Set(addable.length === 1 ? addable.map((entry) => entry.store.id) : []);
  const targets = addable.filter((entry) => selected.has(entry.store.id)).map(({ store }) => store);

  const [prices, setPrices] = useState<PriceChoice>('base');
  // An inactive master cannot go live; the backend would refuse, so start from what it allows.
  const [whenChoice, setWhen] = useState<WhenChoice>(product.status === 'active' ? 'now' : 'draft');
  const when: WhenChoice = prices === 'later' ? 'draft' : whenChoice;
  const [publishAt, setPublishAt] = useState('');
  const [handles, setHandles] = useState<Readonly<Record<string, string>>>({});
  const [failures, setFailures] = useState<ReadonlyMap<string, unknown>>(new Map());
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const firstTarget = targets[0];
  const handleOf = (storeId: string) => handles[storeId] ?? product.handle;
  const handleStores = targets.filter(
    (store) => targets.length === 1 || handleTaken(failures.get(store.id)),
  );
  const badHandle = (storeId: string) => !HANDLE.test(handleOf(storeId).trim());
  const scheduleMissing = when === 'schedule' && !publishAt;

  const toggle = (storeId: string, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(storeId);
    else next.delete(storeId);
    setPicked(next);
  };

  const bodyFor = (store: StaffProfileStoresItem): PostAdminV1StoresStoreIdProductsBody => {
    const handle = handleOf(store.id).trim();
    const at =
      when === 'schedule' ? fromZonedInput(publishAt, displayTimeZone(store.timezone)) : null;
    return {
      productId: product.id,
      status: when === 'now' ? 'published' : 'draft',
      ...(handle === product.handle ? {} : { handle }),
      ...(at ? { publishAt: at } : {}),
    };
  };

  const submit = async () => {
    setShowErrors(true);
    if (targets.length === 0 || scheduleMissing) return;
    if (targets.some((store) => badHandle(store.id))) return;
    setBusy(true);
    const results = await Promise.allSettled(
      targets.map((store) => add.mutateAsync({ storeId: store.id, body: bodyFor(store) })),
    );
    setBusy(false);
    const failed = new Map<string, unknown>();
    results.forEach((result, index) => {
      const store = targets[index];
      if (store && result.status === 'rejected') failed.set(store.id, result.reason);
    });
    const done = targets.length - failed.size;
    if (done > 0) toast.success(t(`storeCatalog:publishModal.toast.${when}`, { count: done }));
    if (failed.size === 0) {
      onOpenChange(false);
      return;
    }
    setFailures(failed);
    setPicked(new Set(failed.keys()));
  };

  const failedEntries = targets.flatMap((store) => {
    const error = failures.get(store.id);
    return error === undefined ? [] : [{ store, error }];
  });
  const conflict = failedEntries.find(({ error }) => publishConflict(error));
  const conflictStatus = conflict ? publishConflict(conflict.error)?.productStatus : undefined;
  const otherFailures = failedEntries.filter(
    ({ error }) => !publishConflict(error) && !handleTaken(error),
  );
  const requestId = (error: unknown) => (isApiError(error) ? error.requestId : undefined);

  return (
    <EditModal
      open
      onOpenChange={onOpenChange}
      title={t('storeCatalog:publishModal.title')}
      description={t('storeCatalog:publishModal.description')}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            disabled={busy || targets.length === 0}
            onClick={() => void submit()}
          >
            {busy
              ? t('storeCatalog:publishModal.working')
              : t(`storeCatalog:publishModal.submit.${when}`, { count: targets.length })}
          </Button>
        </>
      }
    >
      {conflict && conflictStatus ? (
        <PublishConflictAlert
          productId={product.id}
          productStatus={conflictStatus}
          requestId={requestId(conflict.error)}
        />
      ) : null}
      {otherFailures.map(({ store, error }) => {
        const id = requestId(error);
        return (
          <Alert key={store.id} variant="destructive" role="alert">
            <AlertDescription>
              {t('storeCatalog:publishModal.storeFailed', {
                store: store.name,
                reason: message(error),
              })}
              {id ? (
                <span className="block text-xs">
                  {t('errors:requestIdShort', { requestId: id })}
                </span>
              ) : null}
            </AlertDescription>
          </Alert>
        );
      })}

      <Section id={`${ids}-stores`} title={t('storeCatalog:publishModal.stores')}>
        <div className="flex flex-col gap-2.5">
          {inStore.map((entry) => (
            <AlreadyInStore key={entry.store.id} entry={entry} product={product} />
          ))}
          {addable.map(({ store }) => (
            <StoreCheck
              key={store.id}
              store={store.name}
              detail={t('storeCatalog:publishModal.notYet')}
              checked={selected.has(store.id)}
              disabled={busy}
              onChange={(checked) => toggle(store.id, checked)}
            />
          ))}
          {closed.map(({ store }) => (
            <StoreCheck
              key={store.id}
              store={store.name}
              detail={t('storeCatalog:publishModal.closed')}
              checked={false}
              disabled
            />
          ))}
        </div>
        {showErrors && targets.length === 0 && addable.length > 0 ? (
          <p className="text-destructive text-xs" role="alert">
            {t('storeCatalog:publishModal.pickStore')}
          </p>
        ) : null}
      </Section>

      <Section
        id={`${ids}-prices`}
        title={
          targets.length === 1 && firstTarget
            ? t('storeCatalog:publishModal.prices.titleIn', { store: firstTarget.name })
            : t('storeCatalog:publishModal.prices.title')
        }
      >
        <RadioGroup
          value={prices}
          onValueChange={(value) => setPrices(value as PriceChoice)}
          aria-labelledby={`${ids}-prices`}
          className="flex flex-col gap-2.5"
          disabled={busy}
        >
          <Choice value="base" title={t('storeCatalog:publishModal.prices.base')} hint={baseHint} />
          <Choice
            value="later"
            title={t('storeCatalog:publishModal.prices.later')}
            hint={t('storeCatalog:publishModal.prices.laterHint')}
          />
        </RadioGroup>
      </Section>

      <Section id={`${ids}-when`} title={t('storeCatalog:publishModal.when.title')}>
        <RadioGroup
          value={when}
          onValueChange={(value) => setWhen(value as WhenChoice)}
          aria-labelledby={`${ids}-when`}
          className="grid gap-2.5 sm:grid-cols-3"
          disabled={busy}
        >
          {WHEN_CHOICES.map((choice) => (
            <Choice
              key={choice}
              value={choice}
              title={t(`storeCatalog:publishModal.when.${choice}`)}
              disabled={prices === 'later' && choice !== 'draft'}
            />
          ))}
        </RadioGroup>
        {when === 'schedule' ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${ids}-at`}>{t('storeCatalog:fields.publishAt')}</Label>
            <DateTimePicker
              id={`${ids}-at`}
              value={publishAt}
              onChange={setPublishAt}
              timeZone={displayTimeZone(firstTarget?.timezone)}
              aria-invalid={showErrors && scheduleMissing ? true : undefined}
            />
            <span className="text-muted-foreground text-xs">
              {targets.length === 1
                ? t('storeCatalog:publishModal.when.clockOne', {
                    timeZone: displayTimeZone(firstTarget?.timezone),
                  })
                : t('storeCatalog:publishModal.when.clockEach')}
            </span>
            {showErrors && scheduleMissing ? (
              <p className="text-destructive text-xs" role="alert">
                {t('storeCatalog:validation.dateTime')}
              </p>
            ) : null}
          </div>
        ) : null}
        {product.status === 'active' ? null : (
          <p className="text-muted-foreground text-xs">
            {t('storeCatalog:stores.draftBecauseInactive', {
              status: t(`storeCatalog:masterStatus.${product.status}`),
            })}
          </p>
        )}
      </Section>

      {handleStores.map((store) => {
        const inputId = `${ids}-handle-${store.id}`;
        const taken = handleTaken(failures.get(store.id));
        const invalid = showErrors && badHandle(store.id);
        const failure = failures.get(store.id);
        const id = taken ? requestId(failure) : undefined;
        return (
          <div key={store.id} className="flex flex-col gap-1.5 text-sm">
            <Label htmlFor={inputId}>
              {targets.length === 1
                ? t('storeCatalog:publishModal.handle.label')
                : t('storeCatalog:publishModal.handle.labelIn', { store: store.name })}
            </Label>
            <Input
              id={inputId}
              className="font-mono text-[13px]"
              value={handleOf(store.id)}
              aria-invalid={taken || invalid ? true : undefined}
              disabled={busy}
              onChange={(event) => {
                setHandles((current) => ({ ...current, [store.id]: event.target.value }));
              }}
            />
            {taken ? (
              <p className="text-destructive text-xs" role="alert">
                {t('storeCatalog:publishModal.handle.taken', { store: store.name })}
                {id ? (
                  <span className="block">{t('errors:requestIdShort', { requestId: id })}</span>
                ) : null}
              </p>
            ) : invalid ? (
              <p className="text-destructive text-xs" role="alert">
                {t('storeCatalog:validation.handleFormat')}
              </p>
            ) : (
              <span className="text-muted-foreground text-xs">
                {t('storeCatalog:publishModal.handle.hint')}
              </span>
            )}
          </div>
        );
      })}
    </EditModal>
  );
}

/**
 * Publish the master product to more stores, in one action: it acts at once and reports each
 * store's failure, leaving the failed stores ticked to try again. A store the product is already
 * in is shown but not changed; its own page holds unpublish and schedule.
 */
export function PublishToStoresModal({ product, open, onOpenChange }: PublishToStoresModalProps) {
  // Mounted while open, so each opening starts clean.
  return open ? <Body product={product} onOpenChange={onOpenChange} /> : null;
}
